import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { MessageCircle, X, Minus, User } from 'lucide-react';
import './FloatingChatNotification.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const FloatingChatNotification = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const onSupportChatRef = useRef(false);
    onSupportChatRef.current = location.pathname === '/support';

    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [totalUnread, setTotalUnread] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    useEffect(() => {
        initializeSocket();
        loadUnreadCount();

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, []);

    const initializeSocket = () => {
        const newSocket = io(API_BASE_URL, {
            transports: ['websocket'],
            reconnection: true
        });

        newSocket.on('connect', () => {
            const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
            newSocket.emit('authenticate', {
                userId: adminData.id,
                userType: 'admin'
            });
        });

        newSocket.on('new_message_notification', (data) => {
            // On Support chat page the user already sees the thread; avoid duplicate alerts + noise
            if (onSupportChatRef.current) {
                loadUnreadCount();
                return;
            }

            const notification = {
                id: Date.now(),
                roomId: data.roomId,
                message: data.message,
                buyerName: data.message.sender?.business_name || data.message.sender?.name || 'Buyer',
                timestamp: new Date()
            };

            setNotifications(prev => [notification, ...prev].slice(0, 5)); // Keep last 5
            setTotalUnread(prev => prev + 1);
            setIsExpanded(true);
            setIsMinimized(false);

            setTimeout(() => {
                setIsExpanded(false);
            }, 10000);

            playNotificationSound();

            if (Notification.permission === 'granted') {
                new Notification('New Message', {
                    body: `${notification.buyerName}: ${data.message.message || 'Sent a file'}`,
                    icon: '/logo.png',
                    tag: `chat-${data.roomId}`
                });
            }
        });

        setSocket(newSocket);

        // Request notification permission
        if (Notification.permission === 'default') {
            Notification.requestPermission();
        }
    };

    const loadUnreadCount = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/api/chat/rooms`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const unread = data.data.rooms.reduce((sum, room) => sum + (room.unreadCount || 0), 0);
                setTotalUnread(unread);
            }
        } catch (error) {
            console.error('Load unread count error:', error);
        }
    };

    const playNotificationSound = () => {
        // Simple beep sound using Web Audio API
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
            console.error('Audio error:', error);
        }
    };

    const handleNotificationClick = (roomId) => {
        navigate(`/support?room=${roomId}`);
        setIsExpanded(false);

        // Remove this notification
        setNotifications(prev => prev.filter(n => n.roomId !== roomId));
        setTotalUnread(prev => Math.max(0, prev - 1));
    };

    const handleOpenChat = () => {
        navigate('/support');
        setIsExpanded(false);
        setNotifications([]);
    };

    const handleToggleExpand = () => {
        if (isMinimized) {
            setIsMinimized(false);
            setIsExpanded(true);
        } else {
            setIsExpanded(!isExpanded);
        }
    };

    const handleMinimize = (e) => {
        e.stopPropagation();
        setIsMinimized(true);
        setIsExpanded(false);
    };

    const handleDismissAll = (e) => {
        e.stopPropagation();
        setNotifications([]);
        setIsExpanded(false);
    };

    if (isMinimized) {
        return (
            <div className="floating-chat-minimized" onClick={() => setIsMinimized(false)}>
                <MessageCircle size={24} className="minimize-icon" />
                {totalUnread > 0 && (
                    <span className="minimize-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
                )}
            </div>
        );
    }

    return (
        <div className={`floating-chat-notification ${isExpanded ? 'expanded' : ''}`}>
            {/* Main Button */}
            <div className="chat-notification-button" onClick={handleToggleExpand}>
                <MessageCircle size={24} className="chat-icon" />
                {totalUnread > 0 && (
                    <span className="chat-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
                )}
                <span className="chat-label">Messages</span>
            </div>

            {/* Expanded Panel */}
            {isExpanded && (
                <div className="chat-notification-panel">
                    <div className="panel-header">
                        <h4>New Messages ({notifications.length})</h4>
                        <div className="panel-actions">
                            <button
                                className="panel-action-btn"
                                onClick={handleMinimize}
                                title="Minimize"
                            >
                                <Minus size={16} />
                            </button>
                            <button
                                className="panel-action-btn"
                                onClick={handleDismissAll}
                                title="Dismiss all"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="panel-content">
                        {notifications.length === 0 ? (
                            <div className="no-notifications">
                                <p>No new messages</p>
                                <button className="view-all-btn" onClick={handleOpenChat}>
                                    View All Chats
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="notifications-list">
                                    {notifications.map(notification => (
                                        <div
                                            key={notification.id}
                                            className="notification-item"
                                            onClick={() => handleNotificationClick(notification.roomId)}
                                        >
                                            <div className="notification-avatar">
                                                <User size={20} />
                                            </div>
                                            <div className="notification-content">
                                                <div className="notification-header">
                                                    <span className="buyer-name">{notification.buyerName}</span>
                                                    <span className="notification-time">
                                                        {formatTime(notification.timestamp)}
                                                    </span>
                                                </div>
                                                <p className="notification-message">
                                                    {notification.message.message ||
                                                        (notification.message.message_type === 'image' ? 'Image' : 'Document')}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="panel-footer">
                                    <button className="view-all-btn" onClick={handleOpenChat}>
                                        View All Chats ({totalUnread})
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const formatTime = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
};

export default FloatingChatNotification;
